import React from "react";
import { compose } from "recompose";
import { connect } from "react-redux";
import {
    Card,
    CardBody,
    CardTitle,
    Alert,
    Row,
    Col,
} from "reactstrap";
import { FormattedMessage } from "react-intl";

import { Api } from "@api/index";
import { ApiButton } from "@api/components/ApiButton";
import { UploadButton } from "@api/components/UploadButton";
import { File } from "@files/components/File";
import { messages as fMessages } from "@files/messages";
import { withLocale, InjectedLocaleProps } from "@localisation/components/withLocale";
import { Album } from "@music/components/Album";
import { AlbumPreview } from "@music/components/AlbumPreview";
import { messages as mMessages } from "@music/messages";
import { StateError } from "@orm/models";
import { Icon } from "@ui/Icon";
import { ErrorAlert } from "@ui/ErrorAlert";
import { ProgressSteps, ProgressStep } from "@ui/ProgressSteps";
import { getExcelSpreadsheetAccept } from "@util/index";
import { BundleStatus } from "../BundleStatus";
import { Bundle, UploadState } from "../../models";
import { messages } from "../../messages";
import { getBundleSteps } from "../../selectors";

interface BundleViewOwnProps {
    bundle: Bundle;
}

interface BundleViewStateProps {
    steps: ProgressStep[];
}

interface BundleViewState {
    busy: boolean;
    error: StateError;
}

type Props = BundleViewOwnProps & BundleViewStateProps & InjectedLocaleProps;

class BundleView extends React.Component<Props, BundleViewState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            busy: false,
            error: null,
        };
    }

    public render() {
        const { bundle, steps, locale } = this.props;
        const { error, busy } = this.state;
        const reuploadButton = (
            <UploadButton
                disabled={busy}
                onError={(e: StateError) => { this.setState({ error: e, busy: false }); }}
                onSuccess={() => { this.setState({ busy: false, error: null }); }}
                actionCreator={(file: File) => {
                    this.setState({ error: null, busy: true });
                    return Api.bundlesUpdate(bundle.id, file);
                }}
                accept={getExcelSpreadsheetAccept()}
            >
                <FormattedMessage {...messages.reuploadMetadata} />
            </UploadButton>
        );
        let createAlbumMessage = messages.createAlbum;
        if (bundle.albumId != null) {
            createAlbumMessage = messages.updateAlbum;
        }
        return (
            <Card className="bundle-view">
                <CardBody>
                    <ProgressSteps steps={steps} />
                    <CardTitle tag="h2">
                        {bundle.attrs != null
                            ? bundle.attrs.title
                            : <FormattedMessage {...messages.bundleDefaultTitle} values={{id: bundle.id}} />
                        }
                        {" "}
                        <small><BundleStatus value={bundle.status} locale={locale} /></small>
                    </CardTitle>
                    {bundle.user != null &&
                        <h4>
                            <FormattedMessage {...messages.uploadedBy} values={{name: bundle.user.name}} />
                        </h4>
                    }
                    {error != null && <ErrorAlert error={error} />}
                    {bundle.isStepCreateAlbum() &&
                        <div>
                            <hr />
                            <h3><FormattedMessage {...messages.albumPreview} /></h3>
                            <p><FormattedMessage {...messages.instructionsCreateAlbum1} /></p>
                            <p><FormattedMessage {...messages.instructionsCreateAlbum2} /></p>
                            <AlbumPreview album={bundle.attrs} locale={locale} />
                            <p className="mt-3"><FormattedMessage {...messages.instructionsCreateAlbum3} /></p>
                            <ApiButton
                                disabled={busy}
                                color="primary"
                                noOutline={true}
                                actionCreators={[() => {
                                    this.setState({ error: null, busy: true });
                                    return Api.bundlesStoreAlbum(bundle.id);
                                }]}
                                onSuccess={() => { this.setState({ busy: false, error: null }); }}
                            >
                                <FormattedMessage {...createAlbumMessage} />
                            </ApiButton>
                            {" "}
                            {reuploadButton}
                        </div>
                    }
                    {bundle.isStepUploadZip() &&
                        <div>
                            <hr />
                            <p><FormattedMessage {...messages.instructionsUploadZip1} /></p>
                            <ol>
                                <li><FormattedMessage {...messages.instructionsUploadZipList1} /></li>
                                <li>
                                    <FormattedMessage {...messages.instructionsUploadZipList2} />
                                </li>
                                <li><FormattedMessage {...messages.instructionsUploadZipList3} /></li>
                            </ol>
                            <UploadButton
                                color="primary"
                                disabled={busy}
                                onError={(e: StateError) => { this.setState({ error: e, busy: false }); }}
                                onSuccess={() => { this.setState({ busy: false, error: null }); }}
                                actionCreator={(file: File) => {
                                    this.setState({ error: null, busy: true });
                                    return Api.bundlesUploadZip(bundle.id, file);
                                }}
                                accept=".zip,application/zip"
                            >
                                <FormattedMessage {...messages.uploadZip} />
                            </UploadButton>
                            {" "}
                            {reuploadButton}
                        </div>
                    }
                    {bundle.isStepComplete() &&
                        <div>
                            <hr />
                            <p><FormattedMessage {...messages.instructionsComplete1} /></p>
                            <Alert color="warning">
                                <Icon name="exclamation-triangle" />
                                {" "}
                                <FormattedMessage {...messages.instructionsComplete2} />
                                <hr />
                                <p><FormattedMessage {...messages.instructionsComplete3} /></p>
                            </Alert>
                            <ApiButton
                                color="primary"
                                noOutline={true}
                                disabled={busy}
                                actionCreators={[() => {
                                    this.setState({ error: null, busy: true });
                                    return Api.bundlesComplete(bundle.id);
                                }]}
                                onSuccess={() => { this.setState({ busy: false, error: null }); }}
                            >
                                <FormattedMessage {...messages.complete} />
                            </ApiButton>
                            {" "}
                            {reuploadButton}
                            {" "}
                            <UploadButton
                                disabled={busy}
                                onError={(e: StateError) => { this.setState({ error: e, busy: false }); }}
                                onSuccess={() => { this.setState({ busy: false, error: null }); }}
                                actionCreator={(file: File) => {
                                    this.setState({ error: null, busy: true });
                                    return Api.bundlesUploadZip(bundle.id, file);
                                }}
                                accept=".zip,application/zip"
                            >
                                <FormattedMessage {...messages.reuploadZip} />
                            </UploadButton>
                        </div>
                    }
                    {bundle.isStepError() &&
                        <div>
                            <hr />
                            <p><FormattedMessage {...messages.instructionsError1} /></p>
                            {bundle.errors != null &&
                                <Alert color="danger">
                                    <span><FormattedMessage {...messages.instructionsError2} /></span>
                                    <hr />
                                    <ul>
                                        {Object.keys(bundle.errors).map(
                                            (key) => <li key={key}>{key + ": " + bundle.errors[key]}</li>,
                                        )}
                                    </ul>
                                </Alert>
                            }
                        </div>
                    }
                    {bundle.album != null &&
                        <div>
                            <hr />
                            <h3><FormattedMessage {...mMessages.albumEntitySingular} /></h3>
                            <Album album={bundle.album} style="headline" link additionalDetails />
                        </div>
                    }
                    {(bundle.metadataFile != null || bundle.bundleFile != null) &&
                        <div>
                            <hr />
                            <h3><FormattedMessage {...fMessages.fileEntityPlural} /></h3>
                            <Row>
                                <Col>
                                    {bundle.metadataFile != null &&
                                        <File file={bundle.metadataFile} />
                                    }
                                </Col>
                                <Col>
                                    {bundle.bundleFile != null &&
                                        <File file={bundle.bundleFile} />
                                    }
                                </Col>
                            </Row>
                        </div>
                    }
                </CardBody>
            </Card>
        );
    }
}

const mapStateToProps = (state: UploadState, { bundle }: BundleViewOwnProps) => ({
    steps: getBundleSteps(state, {params: {id: String(bundle.id)}}),
});

const ConnectedBundleView = compose<Props, BundleViewOwnProps>(
    connect(mapStateToProps, null),
    withLocale,
)(BundleView);

export {
    ConnectedBundleView as BundleView,
    BundleView as BundleViewPresenter,
};
